import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import auth from '@udacity/ureact-hoth';

import { useFetchMeQuery } from 'app/apis/reviews';
import { isReviewsAccessDenied } from 'app/apis/helpers/reviews-helpers';
import { SidebarLayout } from 'app/common/components/sidebar-layout';
import { LoadingPage } from 'app/common/views/loading-page';
import { useGetMentorQuery } from 'app/mentor/queries/enhanced';
import { actionCreators as ui } from 'app/common/domains/ui/ui-actions';
import { useAppToast } from 'app/common/hooks/use-app-toast';
import plagiarismCaseQueueActions from 'app/queue/domains/plagiarism-case-queue/plagiarism-case-queue-actions';
import questionQueueActions from 'app/queue/domains/question-queue/question-queue-actions';
import {
  startPollingAssignedSubmissions,
  cancelPollingAssignedSubmissions,
} from 'app/queue/domains/submissions/submissions-actions';
import { getAssignedSubmissions, getSubmissionsAsync } from 'app/queue/domains/submissions/submissions-selectors';
import { Overview, History, Statements, CertificationsRoutes } from 'app/queue/views';
import { RootState, useAppDispatch } from 'app/store';

import CONFIG from 'configLoader';
import SidebarNavigation from 'app/queue/views/app/sidebar-navigation';

export const QueueApp: React.FC = () => {
  const assignedSubmissions = useSelector((state: RootState) => getAssignedSubmissions(state));
  const submissionsAsync = useSelector((state: RootState) => getSubmissionsAsync(state, 'fetchAllAssigned'));
  const uid = auth.getCurrentUserId() || '';
  const {
    data: mentorData,
    isLoading: isMentorLoading,
    isFetching: isMentorFetching,
  } = useGetMentorQuery({ uid }, { skip: !uid });
  const { data: reviewsMe, isLoading: isReviewsMeLoading } = useFetchMeQuery();
  const acceptedTerms = reviewsMe?.accepted_terms;

  const prevState = useRef<{
    assignedSubmissions;
    submissionsAsync;
    acceptedTerms: boolean | undefined;
  }>({
    assignedSubmissions,
    submissionsAsync,
    acceptedTerms,
  });

  const { t } = useTranslation();
  const toast = useAppToast();
  const history = useHistory();
  const location = useLocation<{ showCareerRestrictionMessage?: boolean }>();
  const match = useRouteMatch();
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Wait out the refetch after create/update mentor so a fresh agreement does not bounce back here.
    if (mentorData && !isMentorFetching && !mentorData.mentor?.agrees_terms_of_service) {
      history.push('/reviewer-agreement');
    }
  }, [mentorData, isMentorFetching]);

  useEffect(() => {
    dispatch(startPollingAssignedSubmissions());
    dispatch(ui.bootstrapNotifications());
    dispatch(questionQueueActions.bootstrapQueue());
    dispatch(plagiarismCaseQueueActions.bootstrap());

    return () => {
      dispatch(cancelPollingAssignedSubmissions());
    };
  }, []);

  useEffect(() => {
    if (location.state?.showCareerRestrictionMessage) {
      toast({
        variant: 'warning',
        title: t('queue.history.careerReviewRestricted'),
        description: t('queue.history.careerReviewRestrictedDescription'),
        duration: 6000,
      });
      // Clear the state to prevent showing the toast again on refresh
      history.replace(location.pathname, {});
    }
  }, [location.state?.showCareerRestrictionMessage]);

  const displayNewSubmissionToast = () => {
    toast({
      variant: 'info',
      title: t('queue.newAssignment'),
      description: t('queue.assignedANewReview'),
    });

    dispatch(
      ui.addNotification({
        title: t('queue.newAssignment'),
        body: t('queue.assignedANewReview'),
        linkTo: `${CONFIG.dashboardUrl}/queue/overview`,
      })
    );
  };

  const checkForNewSubmission = () => {
    if (
      !prevState.current.submissionsAsync.firstLoad &&
      prevState.current.assignedSubmissions.length < assignedSubmissions.length
    ) {
      displayNewSubmissionToast();
    }
  };

  const checkToSAndRedirect = () => {
    const prevAgrees = prevState.current.acceptedTerms;
    const currAgrees = acceptedTerms;

    if (currAgrees === false && prevAgrees === true) {
      history.push('/reviewer-agreement');
    }
  };

  useEffect(() => {
    checkToSAndRedirect();
    checkForNewSubmission();

    prevState.current = { assignedSubmissions, submissionsAsync, acceptedTerms };
  }, [acceptedTerms, submissionsAsync, assignedSubmissions]);

  // Queue sagas read eligible languages from the GetMentor cache.
  if (isReviewsMeLoading || isMentorLoading) return <LoadingPage />;
  if (isReviewsAccessDenied(reviewsMe)) return null;

  return (
    <div>
      <SidebarLayout leftNav={<SidebarNavigation />}>
        <Switch>
          <Route exact path={`${match.path}`}>
            <Redirect to={`${match.path}/overview`} />
          </Route>
          <Route path={`${match.path}/overview`}>
            <Overview />
          </Route>
          <Route path={`${match.path}/history`}>
            <History />
          </Route>
          <Route path={`${match.path}/statements`}>
            <Statements />
          </Route>
          <Route path={`${match.path}/certifications`}>
            <CertificationsRoutes />
          </Route>
          <Route path={`${match.path}/*`}>
            <Redirect to={`${match.path}/overview`} />
          </Route>
        </Switch>
      </SidebarLayout>
    </div>
  );
};
